import type { DashboardStats } from '@overlayqa/shared';
import { StatBand, StatBandSkeleton } from '../shared/StatBand';

interface QuickStatsSectionProps {
  stats: DashboardStats;
}

interface StatCardConfig {
  key: keyof DashboardStats;
  label: string;
}

const statCards: StatCardConfig[] = [
  { key: 'total', label: 'Total Issues' },
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
  { key: 'thisWeek', label: 'This Week' },
];

// Week-over-week change for the This Week cell, coloured by DIRECTION: up green,
// down red (Emily, 2026-08-10, recorded in memory/user_decisions_log.md). This
// overturns the neutral chip that shipped here, whose rationale was that for a
// bug tracker "more issues" is not inherently good or bad. The chip now reads as
// QA throughput, where moving up is the good direction.
//
// NOTE for anyone reusing this chip elsewhere: the direction semantics are
// specific to throughput. On a surface counting problems found ON the user's
// own site (the Schedule page's findings), more is worse and green-up would
// congratulate them for a regression. That is why ScheduleStatsBand renders no
// trend chip rather than importing this one.
//
// Colour arrives as --color-trend-* tokens, never a Tailwind green/red utility:
// the sweep behind this change found the dashboard using two stock ramps (green
// and emerald) interchangeably for one meaning, both bypassing the token layer
// that semanticColorContrast.source.test.ts holds to 4.5:1.
type Trend = 'up' | 'down';

interface WeekDelta {
  label: string;
  trend: Trend;
}

const TREND_CHIP: Record<Trend, string> = {
  up: 'bg-[var(--color-trend-up-bg)] text-[var(--color-trend-up-text)]',
  down: 'bg-[var(--color-trend-down-bg)] text-[var(--color-trend-down-text)]',
};

function weekDelta(stats: DashboardStats): WeekDelta | null {
  const delta = stats.thisWeek - stats.lastWeek;
  if (delta === 0) return null;
  return {
    label: `${delta > 0 ? '+' : ''}${delta} vs last week`,
    trend: delta > 0 ? 'up' : 'down',
  };
}

export function QuickStatsSection({ stats }: QuickStatsSectionProps) {
  const delta = weekDelta(stats);
  return (
    <StatBand
      id="quick-stats"
      ariaLabel="Quick stats"
      cells={statCards.map((card) => ({
        key: card.key,
        label: card.label,
        value: stats[card.key],
        chip:
          card.key === 'thisWeek' && delta ? (
            <span
              data-trend={delta.trend}
              className={`inline-flex items-center px-2 py-[3px] rounded-full text-xs font-semibold tabular-nums ${TREND_CHIP[delta.trend]}`}
            >
              {delta.label}
            </span>
          ) : undefined,
      }))}
    />
  );
}

export function QuickStatsSkeleton() {
  return <StatBandSkeleton ariaLabel="Quick stats loading" />;
}
